import React from 'react';
import { MessageSquare, Clock, CheckCircle, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LeadForm } from '../components/LeadForm';

const MENTORSHIP_IMAGE = "https://images.unsplash.com/photo-1758519289636-f4ce8e73c484?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmclMjB0d28lMjBwZW9wbGV8ZW58MHx8fHwxNzcyODg0ODkwfDA&ixlib=rb-4.1.0&q=85&w=800";

export const BookCallPage = () => {
  const benefits = [
    'Personalized advice for your specific situation',
    'Direct answers to your immigration questions',
    'Clarity on visa options and pathways',
    'Real-world insights from settled expats',
    'No sales pitch – just honest guidance'
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Fill the Form',
      description: 'Share your details and what you want to discuss'
    },
    {
      step: 2,
      title: 'Get Confirmation',
      description: 'Receive a calendar invite with meeting details'
    },
    {
      step: 3,
      title: 'Join the Call',
      description: '20-minute video call with an expat advisor'
    }
  ];

  return (
    <div data-testid="book-call-page" className="min-h-screen pt-20 pb-16 bg-[#FFFBF8]">
      {/* Header */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#002A76] text-white mb-4 px-3 py-1">
                1:1 Advisory Call
              </Badge>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#002A76] mb-4">
                Get Personalized Guidance from Expats
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                20-minute video call with an advisor who's already made the move to Europe. Ask anything – we'll give you honest answers.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-[#002A76]">
                  <Clock size={18} className="text-[#0AA06A]" />
                  <span className="font-medium">20 mins</span>
                </div>
                <div className="flex items-center gap-2 text-[#002A76]">
                  <MessageSquare size={18} className="text-[#0AA06A]" />
                  <span className="font-medium">Video Call</span>
                </div>
                <div className="flex items-center gap-2 text-[#002A76]">
                  <Calendar size={18} className="text-[#0AA06A]" />
                  <span className="font-medium">Flexible Slots</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src={MENTORSHIP_IMAGE}
                alt="1:1 Advisory Call"
                className="rounded-2xl shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#0AA06A] flex items-center justify-center">
                    <CheckCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#002A76]">500+</p>
                    <p className="text-sm text-gray-500">Calls Completed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Benefits & How It Works */}
            <div className="space-y-12">
              {/* Benefits */}
              <div>
                <h2 className="text-2xl font-bold text-[#002A76] mb-6">What You'll Get</h2>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#0AA06A] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* How It Works */}
              <div>
                <h2 className="text-2xl font-bold text-[#002A76] mb-6">How It Works</h2>
                <div className="space-y-6">
                  {howItWorks.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#002A76] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">{item.step}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#002A76]">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div className="bg-[#F7F4F0] rounded-xl p-6">
                <p className="text-sm text-gray-600">
                  <strong className="text-[#002A76]">Note:</strong> Our calls are free for initial consultations. For extended sessions or specific cases, there may be a nominal fee which will be communicated upfront.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm sticky top-24">
                <h2 className="text-xl font-bold text-[#002A76] mb-2">Book Your Call</h2>
                <p className="text-gray-600 text-sm mb-6">
                  Fill in your details and we'll get back to you within 24 hours with available slots.
                </p>
                <LeadForm source="one_on_one" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookCallPage;
